import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { validateMerchant } from '../../../api/initApi';
import { useDataContext } from '../../../context/dataContainer';
import { FieldComponentProps, IPayError, IPaySuccess } from '../../../types';
import { applePayBtnLimits, clamp, DEFAULT_APPLE_PAY_BUTTON_TYPE, getCanMakeApplePayPayments, isValidApplePayButtonType, sanitizeBusinessName } from './util';
import { getButtonColorUtil, getTopLevelHostname } from '../../../utils';
import { ApplePayButtonTypes } from './types';
import { decryptApplePayData } from '../../../api';
import { DecryptStatus } from '../../../constants';


export const ApplePay: React.FC<FieldComponentProps> = (props: FieldComponentProps) => {
  const [search] = useSearchParams();
  const buttonType = search.get('buttonType');
  const buttonColor = search.get('buttonColor');
  const buttonLocale = search.get('buttonLocale');
  const buttonRadius = search.get('buttonRadius');
  const width = search.get('width');
  const height = search.get('height');
  const includeBIN = search.get('includeBIN') === 'true';
  const businessName = sanitizeBusinessName(search.get('businessName'));

  const { currentLanguage = 'en', toggleFetching, isFetching, sendApplePayTokenToLib, notifyApplePayBtnClicked } = props;
  const { amount, paymentMethod, currencyCode, style, encryption, softDescriptors, description } = useDataContext();

  const totalLabel = businessName || softDescriptors?.merchantName || description;

  const validBtnType = isValidApplePayButtonType(buttonType)
    ? buttonType
    : isValidApplePayButtonType(style?.applePayButtonType)
      ? (style?.applePayButtonType as ApplePayButtonTypes)
      : DEFAULT_APPLE_PAY_BUTTON_TYPE;

  const canMakePayments = useMemo(() => getCanMakeApplePayPayments(), []);

  const getISO3166CodeFromCurrency = (currencyCode: string | undefined)
  : { iso3166Code: string; iso3166CurrencyCode: string } => {
    switch (currencyCode) {
      case '124':
        return { iso3166Code: 'CA', iso3166CurrencyCode: 'CAD' };
      default:
        return { iso3166Code: 'US', iso3166CurrencyCode: 'USD' };
    }
  };

  const handleStartPayment = () => {
    notifyApplePayBtnClicked?.();

    const ApplePaySession = window.ApplePaySession;

    if (!ApplePaySession) {
      console.error('Apple Pay is not supported on this browser.');
      sendApplePayTokenToLib({ 
        status: DecryptStatus.FAILED,
        message: 'Apple Pay is not supported on this browser.',
      });
      return;
    }
    toggleFetching(true);

    const shippingContactFields: ContactFields[] = [];
    const billingContactFields: ContactFields[] = ['postalAddress', 'email'];
    const { iso3166Code, iso3166CurrencyCode } = getISO3166CodeFromCurrency(currencyCode);
    const paymentRequest = {
      countryCode: iso3166Code,
      currencyCode: iso3166CurrencyCode,
      total: {
        label: totalLabel,
        amount: String(amount),
      },
      supportedNetworks: paymentMethod?.credit
        ? paymentMethod.credit.map((el) => el.toLowerCase())
        : ['visa', 'masterCard', 'amex', 'discover', 'jcb'],
      merchantCapabilities: ['supports3DS'],
      requiredBillingContactFields: billingContactFields,
      requiredShippingContactFields: shippingContactFields,
    };

    const apSession = new ApplePaySession(3, paymentRequest);

    apSession.onvalidatemerchant = async (event) => {
      try {
        console.log('event', event);
        const domain = getTopLevelHostname();
        const merchantSession = await validateMerchant({ validationUrl: event.validationURL, domain });
        console.log('event2', merchantSession);

        apSession.completeMerchantValidation(merchantSession);
      } catch (e) {
        toggleFetching(false);
        sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: (e as Error).message });
        console.log('ApplePay error: ' + e);
      }
    };

    apSession.onpaymentauthorized = async (event) => {
      try {
        if (!encryption || !encryption.key || !encryption.keyId) {
          sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: 'Public key is missing' });
          return;
        }

        const response = await decryptApplePayData({
          applePaymentData: event.payment.token,
          publicKey: encryption.key,
          keyId: encryption.keyId,
          includeBIN,
        });

        if ((response as IPayError).title) {
          const { title } = response as IPayError;
          sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: title });
          toggleFetching(false);
          return;
        }

        sendApplePayTokenToLib({ status: DecryptStatus.SUCCESS, data: response as IPaySuccess });
        apSession.completePayment(ApplePaySession.STATUS_SUCCESS);
      } catch (e) {
        sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: (e as Error).message });
        toggleFetching(false);
      }
    };

    apSession.oncancel = () => {
      sendApplePayTokenToLib({ status: DecryptStatus.CANCELLED });
      toggleFetching(false);
    };

    apSession.begin();
  };

  if (!canMakePayments) {
    return <></>;
  }

  return (
    <div>
      <button
        className={classNames(
          'btn',
          'apple-pay-btn',
          (buttonColor || getButtonColorUtil(style?.bodyBg)),
          validBtnType,
        )}
        lang={buttonLocale || currentLanguage || 'en-US'}
        onClick={handleStartPayment}
        disabled={isFetching}
        style={{
          borderRadius: clamp(buttonRadius, applePayBtnLimits.borderRadius()),
          height: clamp(height, applePayBtnLimits.height(validBtnType)),
          width: clamp(width, applePayBtnLimits.width(validBtnType)),
        }}
      >
      </button>
    </div>
  );
};

